import { Component, ViewChild, ElementRef, Injector } from '@angular/core';
import { Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import { AdiantamentoDocumentoService } from '../service/adiantamento-documento.service';
import { NotaAdiantamento } from '../relatorio/nota-adiantamento';
import {
  BaseResourceFormComponent, Adiantamento, LoginContabil,
  EddyAutoComplete, Empenho, AdiantamentoDocumento, FuncaoService,
  DateFormatPipe, OrgaoAssinaturaService
} from 'eddydata-lib';
import { EmpenhoService, AdiantamentoService, PagamentoService } from 'administrativo-lib';

@Component({
  selector: 'lib-adiantamento-form',
  templateUrl: './adiantamento-form.component.html'
})
export class AdiantamentoFormComponent extends BaseResourceFormComponent<Adiantamento, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  @ViewChild('oficio') inputField: ElementRef;

  public listaSituacoes: Array<any>;
  public listaEspecies: Array<any>;
  public listaTipos: Array<any>;
  public empenhoAutoComplete: EddyAutoComplete<Empenho>;
  public listaPareceres: Array<any>;
  public saldo: number;
  public vlEfetivado: number;
  public vlAnulado: number;
  public listaDocumentos: Array<AdiantamentoDocumento>;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    private messageService: MessageService,
    private funcaoService: FuncaoService,
    protected empenhoService: EmpenhoService,
    protected pagtoService: PagamentoService,
    protected assinaturaService: OrgaoAssinaturaService,
    protected documentoService: AdiantamentoDocumentoService,
    protected adiantamentoService: AdiantamentoService) {
    super(new Adiantamento(), injector, Adiantamento.converteJson, adiantamentoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      numero: [null, [Validators.required]],
      mes: [null],
      data_adiantamento: [null, [Validators.required]],
      data_vencimento: [null, [Validators.required]],
      data_liberacao: [null],
      data_prestacao: [null],
      data_partida: [null],
      data_chegada: [null],
      parecer: [null],
      descricao_parecer: [null],
      especie: [null, [Validators.required]],
      situacao: [null, [Validators.required]],
      valor_adiantado: [null, [Validators.required]],
      valor_complemento: [0],
      oficio: [null, [Validators.required]],
      motivo: [null, [Validators.required]],
      numero_conta: [null],
      agencia: [null],
      banco: [null],
      beneficiario: [null, [Validators.required]],
      tipo_beneficiario: [null, [Validators.required]],
      cargo_beneficiario: [null, [Validators.required]],
      destino: [null],
      empenho: [null, [Validators.required]],
      orgao: [this.login.orgao, [Validators.required]],
      exercicio: [this.login.exercicio],
      aux: []
    });
  }

  protected parametrosExtras(): {} {
    return { relations: 'empenho,empenho.exercicio,exercicio,orgao' };
  }

  protected afterInit(): void {
    this.listaSituacoes = [
      { id: 'A', nome: 'ATIVO' },
      { id: 'I', nome: 'INATIVO' }
    ];
    this.listaTipos = [
      { id: 'S', nome: 'SERVIDOR PÚBLICO' },
      { id: 'A', nome: 'AGENTE POLÍTICO' },
      { id: 'V', nome: 'VEREADOR' },
      { id: 'O', nome: 'OUTRO' }
    ];
    this.listaEspecies = [
      { id: 'N', nome: 'NUMERARIAS' },
      { id: 'D', nome: 'DIÁRIAS' }
    ];
    this.listaPareceres = [
      { id: 'F', nome: 'FAVORÁVEL' },
      { id: 'D', nome: 'DESFAVORÁVEL' }
    ];
    this.carregarAutoCompletes();
    this.antesInserir();
  }

  protected campoFoco(): ElementRef {
    return this.inputField;
  }

  protected afterLoad() {
    this.entidade.data_adiantamento = new DateFormatPipe().transform(this.entidade.data_adiantamento, []);
    this.entidade.data_vencimento = new DateFormatPipe().transform(this.entidade.data_vencimento, []);
    this.entidade.data_liberacao = new DateFormatPipe().transform(this.entidade.data_liberacao, []);
    this.entidade.data_prestacao = new DateFormatPipe().transform(this.entidade.data_prestacao, []);
    this.entidade.data_partida = new DateFormatPipe().transform(this.entidade.data_partida, []);
    this.entidade.data_chegada = new DateFormatPipe().transform(this.entidade.data_chegada, []);
    // this.entidade.id ?
    //   this.entidadeForm.get('numero').disable() :
    //   this.entidadeForm.get('numero').enable();
    // this.entidade.id ?
    //   this.entidadeForm.get('data_adiantamento').disable() :
    //   this.entidadeForm.get('data_adiantamento').enable();
    // this.entidade.empenho ?
    //   this.entidadeForm.get('valor_adiantado').disable() :
    //   this.entidadeForm.get('valor_adiantado').enable();
    // this.entidade.data_prestacao ?
    //   this.entidadeForm.get('data_prestacao').disable() :
    //   this.entidadeForm.get('data_prestacao').enable();
    this.entidade.parecer ?
      this.entidadeForm.get('parecer').disable() :
      this.entidadeForm.get('parecer').enable();
    this.calcularSaldo();
    this.preencherDocumento();
    this.empenhoAutoComplete.id = this.entidade.empenho ? this.entidade.empenho.id : null;
  }

  protected beforeSubmit() {
    try {
      if (!this.entidadeForm.get('valor_adiantado') || Number(this.entidadeForm.get('valor_adiantado').value) === 0) {
        throw new Error('O valor do adiantamento tem que ser maior que zero!');
      }
      this.entidadeForm.get('aux').setValue(0);
      this.entidadeForm.get('data_adiantamento').enable();
      this.entidadeForm.get('data_prestacao').enable();
      this.entidadeForm.get('valor_adiantado').enable();
      this.entidadeForm.get('parecer').enable();
      const dtAdiantamento: Date = this.entidadeForm.get('data_adiantamento').value;
      const dtVencimento: Date = this.entidadeForm.get('data_vencimento').value;
      if (dtAdiantamento.getFullYear() !== this.login.exercicio.ano) {
        throw new Error('O ano da data do adiantamento está diferente do exercício logado');
      }
      if (dtVencimento.getTime() < dtAdiantamento.getTime()) {
        throw new Error('A data de vencimento não pode ser menor que a data do adiantamento.');
      }
      this.entidadeForm.get('mes').setValue(dtAdiantamento.getUTCMonth() + 1);
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  protected afterSubmit(ent: Adiantamento) {
    window.scrollTo(0, 0);
    setTimeout(() => this.inputField.nativeElement.focus());
    if (this.currentActionRoute === 'novo') {
      this.router.navigate(['/adiantamentos', 'novo', ent.id]);
    } else {
      this.router.navigate(['/adiantamentos', ent.id, 'editar']);
    }
  }

  public preencherDocumento() {
    // documentos do adiantamento
    this.documentoService.obterPorAdiantamento(this.entidade.id).pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (data: any) => {
          this.listaDocumentos = data ? data.content : new Array<AdiantamentoDocumento>();
        }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
      );
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  private antesInserir() {
    if (this.currentActionRoute === 'novo') {
      this.obterUltimoAdiantamento();
      this.entidadeForm.get('data_adiantamento').setValue(new Date());
      this.entidadeForm.get('situacao').setValue('A');
    }
  }

  public preencherFavorecido(event: any) {
    this.entidadeForm.get('beneficiario').setValue(event.favorecido.nome);
    this.entidadeForm.get('valor_adiantado').setValue(event.valor_empenho);
    console.log(event);
    if (!this.entidade.empenho) {
      this.entidade.empenho = event;
      this.entidade.exercicio = this.login.exercicio;
      this.entidade.orgao = this.login.orgao;
    }
    this.calcularSaldo();
  }

  public async calcularSaldo() {
    if (this.entidadeForm.get('empenho')) {
      this.vlEfetivado = await this.empenhoService.obterValorEmpenho(
        this.entidade.empenho.numero, this.entidade.exercicio.id, this.entidade.orgao.id
      ).toPromise();
    } else {
      this.vlEfetivado = this.entidadeForm.get('valor_adiantado').value ?
        +this.entidadeForm.get('valor_adiantado').value : +this.entidade.valor_adiantado;
    }
    this.vlAnulado = await this.pagtoService.obterTotalPagoAnuladoPorEmpenho(this.entidade.empenho.numero, this.entidade.exercicio.id, this.entidade.orgao.id
    ).toPromise();
    this.saldo = this.vlEfetivado - this.vlAnulado;
  }

  private obterUltimoAdiantamento() {
    this.adiantamentoService.obterUtilmoAdiantamento(this.login.orgao.id, this.login.exercicio.id).pipe(takeUntil(this.unsubscribe))
      .subscribe((numero) => {
        this.entidadeForm.get('numero').setValue(this.funcaoService.strZero(numero, 5));
      });
  }

  private carregarAutoCompletes() {
    // autocomplete para empenho
    this.empenhoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('empenho'), this.empenhoService,
      'id', ['numero', 'exercicio.ano'], {
      especie: 'EMO',
      exercicio_id: this.login.exercicio.id, orgao_id: this.login.orgao.id,
      relations: 'exercicio,orgao,favorecido,subelemento,ficha,ficha.acao', orderBy: 'numero'
    }, { number: ['numero'] }
    );
  }

  public salvarPrestacao() {
    this.beforeSubmit();
    this.entidadeForm.get('data_adiantamento').enable();
    this.entidadeForm.get('valor_adiantado').enable();
    const entidade: Adiantamento = this.jsonDataToResourceFn(this.entidadeForm.value);
    this.adiantamentoService.salvarPrestacao(entidade).pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.messageService.add({ severity: 'success', summary: 'Informação', detail: 'Prestação de contas salva com sucesso' });
      },
        (error) => {
          this.messageService.add(
            { severity: 'error', summary: 'Atenção!', detail: error.error && error.error.payload ? error.error.payload : error }
          );
          this.cancelarPrestacao();
        });
  }

  public cancelarPrestacao() {
    this.entidadeForm.get('data_prestacao').setValue(null);
    this.entidadeForm.get('descricao_parecer').setValue(null);
    this.entidadeForm.get('parecer').setValue(null);
    this.loadResource();
  }

  public imprimir() {
    const parametros = {};

    let relations = '';
    relations += 'empenho.ficha.executora.unidade,empenho.ficha.despesa,empenho.ficha.funcao,empenho.ficha.subfuncao,';
    relations += 'empenho.ficha.programa,empenho.ficha.acao,empenho.ficha.aplicacao,';
    relations += 'empenho.licitacao,empenho.modalidade,empenho.subelemento,empenho.favorecido';

    parametros['relations'] = relations;
    parametros['exercicio_id'] = this.login.exercicio.id;
    parametros['orgao_id'] = this.login.orgao.id;
    parametros['id'] = this.entidadeForm.get('id').value;
    // parametros['impresso'] = this.selectNaoImpresso;
    this.adiantamentoService
      .extendido(1, -1, parametros)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(lista => {
        const nota = new NotaAdiantamento(this.assinaturaService);
        nota.imprimir(lista.content, this.login);
      },
        (error) => this.messageService.add(
          { severity: 'error', summary: 'Atenção!', detail: error.error && error.error.payload ? error.error.payload : error }
        )
      );
  }

  public voltar() {
    switch (this.login.sistema) {
      case 'contabil':
      case 'controle-interno':
        this.router.navigate(['/contabilidade']);
        break;
      default:
        this.sair();
        break;
    }
  }
}
